import { useMemo, useState, type JSX, type KeyboardEvent, type MouseEvent } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { AlertTriangle, Ban, Loader2, Lock, Pencil, Plus, Power, PowerOff, Swords, Trash2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminChallenges,
  useAdminChallengeStandings,
  useAdminInviters,
  useAdminTeams,
  useAdminNiches,
  useCreateChallenge,
  useUpdateChallenge,
  useDeleteChallenge,
  useApproveChallengePrize,
  useRejectChallengePrize,
} from '@/hooks/useAdmin';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/ui/pill';
import { Tag } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ChallengeMetric, ChallengePrizePending, OutreachChallenge } from '@/api/types';
import { CHALLENGE_METRICS } from '@/api/types';
import {
  challengeState,
  isChallengeLocked,
  pendingPrizeCount,
  sortChallengesForReview,
  tiedLeaders,
} from '@/lib/challenges';
import { formatDate } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import { teamLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';

/**
 * What each challenge metric ranks, in words. Local to this page: the shared
 * CHALLENGE_METRIC_LABELS in api/types still carries the older wording for
 * the outreacher dashboard.
 */
const METRIC_COPY: Record<ChallengeMetric, { label: string; hint: string }> = {
  MOST_INVITES: {
    label: 'Most invites logged',
    hint: 'Invites each outreacher logged between the start and end dates.',
  },
  MOST_CONVERSIONS: {
    label: 'Most creators posted',
    hint: 'Creators each outreacher logged who first posted between the start and end dates.',
  },
  HIGHEST_CONVERSION_RATE: {
    label: 'Highest post rate',
    hint: 'Posted ÷ invites logged between the dates. Outreachers need at least 5 invites logged to rank.',
  },
  MOST_POSTS: {
    label: 'Most posts',
    hint: "Approved posts by each outreacher's creators, made between the start and end dates.",
  },
  MOST_VIEWS: {
    label: 'Most views',
    hint: "Views on clips each outreacher's creators posted between the start and end dates.",
  },
};
const metricLabel = (m: string): string => METRIC_COPY[m as ChallengeMetric]?.label ?? 'Unknown metric';

/** What the standings count, pinned to the challenge's own dates. */
function windowCopy(c: Pick<OutreachChallenge, 'metric' | 'startDate' | 'endDate'>): string {
  const range = `between ${formatDate(c.startDate)} and ${formatDate(c.endDate)}`;
  switch (c.metric) {
    case 'MOST_CONVERSIONS':
      return `Counts only creators who first posted ${range}.`;
    case 'MOST_POSTS':
      return `Counts only approved posts made ${range}.`;
    case 'MOST_VIEWS':
      return `Counts only views on clips posted ${range}.`;
    default:
      return `Counts only invites logged ${range}.`;
  }
}

function formatValue(metric: string, value: number): string {
  return metric === 'HIGHEST_CONVERSION_RATE' ? `${value}%` : value.toLocaleString();
}

const hasCashPrize = (c: Pick<OutreachChallenge, 'prizeAmount'>): boolean => !!c.prizeAmount && Number(c.prizeAmount) > 0;

interface ChallengeForm {
  name: string;
  description: string;
  metric: ChallengeMetric;
  targetTeam: string;
  targetNiche: string;
  startDate: string;
  endDate: string;
  prizeAmount: string;
  prizeDescription: string;
}

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function blankForm(): ChallengeForm {
  return {
    name: '',
    description: '',
    metric: 'MOST_INVITES',
    targetTeam: '',
    targetNiche: '',
    startDate: toLocalDatetime(new Date().toISOString()),
    endDate: '',
    prizeAmount: '',
    prizeDescription: '',
  };
}

export function AdminChallenges(): JSX.Element {
  const challengesQuery = useAdminChallenges();
  const createMut = useCreateChallenge();
  const updateMut = useUpdateChallenge();
  const deleteMut = useDeleteChallenge();
  const teamsQuery = useAdminTeams();
  const nichesQuery = useAdminNiches();
  const invitersQuery = useAdminInviters({ limit: 200 });
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<OutreachChallenge | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const standingsQuery = useAdminChallengeStandings(selectedId);

  const createForm = useForm<ChallengeForm>({ defaultValues: blankForm() });
  const editForm = useForm<ChallengeForm>();

  const teamName = useMemo(() => {
    const map = new Map((teamsQuery.data ?? []).map((t) => [t.code, t.displayName]));
    return (code: string) => teamLabel(code, map.get(code));
  }, [teamsQuery.data]);
  const nicheName = useMemo(() => {
    const map = new Map((nichesQuery.data ?? []).map((n) => [n.code, n.displayName]));
    return (code: string) => map.get(code) ?? code;
  }, [nichesQuery.data]);
  const inviterName = useMemo(() => {
    const map = new Map((invitersQuery.data ?? []).map((i) => [i.id, i.name]));
    return (id: number) => map.get(id) ?? null;
  }, [invitersQuery.data]);

  const teamOptions = (teamsQuery.data ?? [])
    .filter((t) => t.isActive)
    .map((t) => ({ value: t.code, label: t.displayName }));
  // Hidden niches can't be picked as a new target; a challenge already
  // targeting one keeps it (labelled) while being edited.
  const nicheOptions = (nichesQuery.data ?? [])
    .filter((n) => !n.isHidden || n.code === editTarget?.targetNiche)
    .map((n) => ({ value: n.code, label: n.displayName, ...(n.isHidden ? { hint: 'Hidden' } : {}) }));

  function openCreate(): void {
    createForm.reset(blankForm());
    setShowCreate(true);
  }

  function openEdit(c: OutreachChallenge): void {
    if (isChallengeLocked(c)) return;
    setEditTarget(c);
    editForm.reset({
      name: c.name,
      description: c.description ?? '',
      metric: c.metric,
      targetTeam: c.targetTeam ?? '',
      targetNiche: c.targetNiche ?? '',
      startDate: toLocalDatetime(c.startDate),
      endDate: toLocalDatetime(c.endDate),
      prizeAmount: c.prizeAmount ?? '',
      prizeDescription: c.prizeDescription ?? '',
    });
  }

  function datesOk(data: ChallengeForm): boolean {
    if (new Date(data.endDate) <= new Date(data.startDate)) {
      toast.error('The end date must be after the start date.');
      return false;
    }
    return true;
  }

  function onCreate(data: ChallengeForm): void {
    if (!datesOk(data)) return;
    createMut.mutate(
      {
        name: data.name,
        description: data.description || undefined,
        metric: data.metric,
        targetTeam: data.targetTeam || null,
        targetNiche: data.targetNiche || null,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        prizeAmount: data.prizeAmount || null,
        prizeDescription: data.prizeDescription || null,
      },
      {
        onSuccess: () => {
          toast.success('Challenge created');
          setShowCreate(false);
          createForm.reset(blankForm());
        },
        onError: (err: Error) => toast.error(`Couldn't create the challenge: ${err.message}`),
      },
    );
  }

  function onEdit(data: ChallengeForm): void {
    if (!editTarget || !datesOk(data)) return;
    updateMut.mutate(
      {
        id: editTarget.id,
        name: data.name,
        description: data.description || null,
        metric: data.metric,
        targetTeam: data.targetTeam || null,
        targetNiche: data.targetNiche || null,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        prizeAmount: data.prizeAmount || null,
        prizeDescription: data.prizeDescription || null,
      },
      {
        onSuccess: () => {
          toast.success('Challenge updated');
          setEditTarget(null);
        },
        onError: (err: Error) => toast.error(`Couldn't save the challenge: ${err.message}`),
      },
    );
  }

  function toggleActive(c: OutreachChallenge): void {
    updateMut.mutate(
      { id: c.id, isActive: !c.isActive },
      {
        onSuccess: () => toast.success(c.isActive ? 'Challenge paused' : 'Challenge resumed'),
        onError: (err: Error) => toast.error(`Couldn't update the challenge: ${err.message}`),
      },
    );
  }

  async function remove(c: OutreachChallenge): Promise<void> {
    try {
      await deleteMut.mutateAsync(c.id);
      toast.success('Challenge deleted');
      if (selectedId === c.id) setSelectedId(null);
    } catch (err) {
      toast.error(`Couldn't delete the challenge: ${(err as Error).message}`);
    }
  }

  const challenges = useMemo(() => sortChallengesForReview(challengesQuery.data ?? []), [challengesQuery.data]);
  const awaitingApproval = pendingPrizeCount(challenges);
  const now = new Date();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Challenges"
        description="Time-boxed competitions between outreachers. When one ends, its final standings are frozen and the prize waits for your approval. Nothing is paid until you approve it."
        actions={
          <>
            {awaitingApproval > 0 && (
              <Pill tone="pending" icon={Trophy} data-testid="prize-approvals-count">
                {awaitingApproval === 1 ? '1 prize needs approval' : `${awaitingApproval} prizes need approval`}
              </Pill>
            )}
            <Button onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" /> New challenge
            </Button>
          </>
        }
      />

      {challengesQuery.isLoading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : challenges.length === 0 ? (
        <EmptyState title="No challenges yet" hint="Create one to start a competition between outreachers." />
      ) : (
        <div className="space-y-3">
          {challenges.map((c) => {
            const state = challengeState(c, now);
            const isSelected = selectedId === c.id;
            const locked = isChallengeLocked(c);
            const toggle = () => setSelectedId(isSelected ? null : c.id);
            const onCardClick = (e: MouseEvent<HTMLDivElement>) => {
              if ((e.target as HTMLElement).closest('button,a,[role="dialog"]')) return;
              toggle();
            };
            const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
              }
            };
            const winnerName =
              c.prizeAwardedToInviterId != null
                ? (inviterName(c.prizeAwardedToInviterId) ??
                  c.prizePending?.standings.find((s) => s.inviterId === c.prizeAwardedToInviterId)?.name ??
                  (isSelected
                    ? standingsQuery.data?.standings.find((s) => s.inviterId === c.prizeAwardedToInviterId)
                        ?.inviterName
                    : null) ??
                  'an outreacher no longer listed')
                : c.prizeDecision?.decision === 'approved'
                  ? (c.prizePending?.inviterName ?? null)
                  : null;
            const awardedAt = c.prizeAwardedAt ?? (c.prizeDecision?.decision === 'approved' ? c.prizeDecision.decidedAt : null);

            return (
              <Card
                key={c.id}
                data-testid={`challenge-${c.publicId}`}
                className={cn(
                  'transition-colors',
                  state === 'needsApproval' && 'border-warning/50',
                  isSelected ? 'ring-1 ring-brand/40' : 'hover:border-border/80',
                )}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isSelected}
                      aria-controls={`standings-${c.id}`}
                      aria-label={`${c.name}: ${isSelected ? 'hide' : 'show'} standings`}
                      onClick={onCardClick}
                      onKeyDown={onKey}
                      className="min-w-0 flex-1 cursor-pointer space-y-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Swords className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <span className="truncate font-semibold">{c.name}</span>
                        {state === 'live' && <Pill tone="success">Live</Pill>}
                        {state === 'ended' && <Pill tone="muted">Ended</Pill>}
                        {state === 'paused' && <Pill tone="muted">Paused</Pill>}
                        {state === 'upcoming' && <Pill tone="pending">Upcoming</Pill>}
                        {state === 'needsApproval' && (
                          <Pill tone="pending" icon={Trophy}>
                            Needs your approval
                          </Pill>
                        )}
                        {state === 'rejected' && <Pill tone="rejected">Prize rejected</Pill>}
                        {state === 'awarded' && (
                          <Pill tone="paid" icon={Trophy}>
                            {winnerName ? 'Prize awarded' : 'Ended, no winner'}
                          </Pill>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Tag>{metricLabel(c.metric)}</Tag>
                        <Tag>{c.targetTeam ? teamName(c.targetTeam) : 'All teams'}</Tag>
                        {c.targetNiche && <Tag tone="niche">{nicheName(c.targetNiche)}</Tag>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(c.startDate)} – {formatDate(c.endDate)}
                        {c.prizeAmount && (
                          <span className="ml-2 font-semibold text-foreground">
                            · ${formatMoney(c.prizeAmount)} prize
                          </span>
                        )}
                        {c.prizeDescription && <span className="ml-1">({c.prizeDescription})</span>}
                      </p>
                      {state === 'awarded' &&
                        awardedAt &&
                        (winnerName ? (
                          <p className="text-success-strong text-xs font-medium">
                            {winnerName} won
                            {hasCashPrize(c) ? ` $${formatMoney(c.prizeAmount)}` : ''} · approved{' '}
                            {formatDate(awardedAt)}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Closed {formatDate(awardedAt)} with nobody in the standings, so no prize was paid.
                          </p>
                        ))}
                      {state === 'rejected' && c.prizeDecision && (
                        <p className="text-xs text-muted-foreground">
                          Rejected {formatDate(c.prizeDecision.decidedAt)}
                          {decidedByLabel(c.prizeDecision.decidedBy)}, so no prize was paid
                          {c.prizeDecision.reason ? `: “${c.prizeDecision.reason}”` : '.'}
                        </p>
                      )}
                      {state === 'ended' && (
                        <p className="text-xs text-muted-foreground">
                          {c.isActive
                            ? 'Final standings are frozen at the next daily prize run. The prize then waits here for your approval.'
                            : 'Paused, so no prize will be proposed unless it is resumed.'}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 gap-1">
                      {locked ? (
                        <span
                          className="inline-flex h-9 items-center gap-1 px-2 text-xs text-muted-foreground"
                          title={
                            state === 'needsApproval'
                              ? 'The final standings are frozen, so this challenge can no longer be edited.'
                              : 'The prize has been decided, so this challenge can no longer be edited.'
                          }
                        >
                          <Lock className="h-3.5 w-3.5" aria-hidden /> Locked
                        </span>
                      ) : (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Edit challenge"
                            aria-label={`Edit ${c.name}`}
                            onClick={() => openEdit(c)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            title={c.isActive ? 'Pause challenge' : 'Resume challenge'}
                            aria-label={c.isActive ? `Pause ${c.name}` : `Resume ${c.name}`}
                            onClick={() => toggleActive(c)}
                            disabled={updateMut.isPending}
                          >
                            {c.isActive ? (
                              <PowerOff className="text-destructive-strong h-4 w-4" />
                            ) : (
                              <Power className="text-success-strong h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      <ConfirmDialog
                        trigger={
                          <Button size="icon" variant="ghost" title="Delete challenge" aria-label={`Delete ${c.name}`}>
                            <Trash2 className="text-destructive-strong h-4 w-4" />
                          </Button>
                        }
                        tone="destructive"
                        title={`Delete “${c.name}”?`}
                        description={
                          c.prizeAwardedAt
                            ? 'This removes the challenge and its standings for good. A prize that was already paid stays paid.'
                            : 'This removes the challenge and its standings for good, and no prize will be paid for it.'
                        }
                        confirmLabel="Delete challenge"
                        pending={deleteMut.isPending}
                        onConfirm={() => remove(c)}
                      />
                    </div>
                  </div>

                  {state === 'needsApproval' && c.prizePending && (
                    <PrizeApproval challenge={c} pending={c.prizePending} />
                  )}

                  {isSelected && (
                    <div id={`standings-${c.id}`} className="mt-4 border-t pt-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <Trophy className="h-3 w-3" aria-hidden />{' '}
                          {state === 'live' || state === 'paused' || state === 'upcoming' ? 'Live standings' : 'Standings'} ·{' '}
                          {metricLabel(c.metric)}
                        </div>
                        <p className="text-xs font-medium text-foreground">{windowCopy(c)}</p>
                        <p className="text-xs text-muted-foreground">{METRIC_COPY[c.metric]?.hint}</p>
                      </div>
                      {standingsQuery.isLoading ? (
                        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading standings…
                        </div>
                      ) : !standingsQuery.data?.standings?.length ? (
                        <p className="py-2 text-sm text-muted-foreground">
                          No standings yet. Waiting for activity inside the challenge dates.
                        </p>
                      ) : (
                        <ol className="mt-2 space-y-1">
                          {standingsQuery.data.standings.map((s) => (
                            <li
                              key={s.inviterId}
                              className="flex items-center gap-3 rounded px-2 py-1.5 text-sm odd:bg-muted/20"
                            >
                              <span className="w-6 text-center text-xs font-bold text-muted-foreground">{s.rank}</span>
                              <span className="flex-1 truncate">{s.inviterName}</span>
                              <Tag>{teamName(s.team)}</Tag>
                              <span className="text-xs font-medium tabular-nums">{formatValue(c.metric, s.value)}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New challenge</DialogTitle>
          </DialogHeader>
          <ChallengeFields
            form={createForm}
            idPrefix="ch"
            teamOptions={teamOptions}
            nicheOptions={nicheOptions}
            onSubmit={onCreate}
            onCancel={() => setShowCreate(false)}
            pending={createMut.isPending}
            submitLabel="Create challenge"
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit challenge</DialogTitle>
            {editTarget && new Date(editTarget.startDate) <= now && (
              <DialogDescription>
                This challenge has started. Changing the metric or dates changes the standings everyone sees.
              </DialogDescription>
            )}
          </DialogHeader>
          <ChallengeFields
            form={editForm}
            idPrefix="ed"
            teamOptions={teamOptions}
            nicheOptions={nicheOptions}
            onSubmit={onEdit}
            onCancel={() => setEditTarget(null)}
            pending={updateMut.isPending}
            submitLabel="Save changes"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** " by Jo" for a readable decider; Clerk user ids aren't names, so they're left out. */
function decidedByLabel(by: string | null | undefined): string {
  if (!by || /^user_/.test(by)) return '';
  return ` by ${by}`;
}

type ApprovalStep = 'review' | 'approve' | 'reject';

/**
 * An ended challenge's prize, waiting on an admin: the frozen final standings
 * (top 5), the proposed winner and the prize, with Approve (pick the winner
 * first when first place is tied) and Reject (with a reason). Both confirm
 * in place before anything is sent.
 */
function PrizeApproval({
  challenge: c,
  pending,
}: {
  challenge: OutreachChallenge;
  pending: ChallengePrizePending;
}): JSX.Element {
  const approveMut = useApproveChallengePrize();
  const rejectMut = useRejectChallengePrize();
  const [step, setStep] = useState<ApprovalStep>('review');
  const [picked, setPicked] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const standings = pending.standings;
  const top = standings.slice(0, 5);
  const tied = pending.tie ? tiedLeaders(standings) : [];
  const tiedIds = new Set(tied.map((s) => s.inviterId));
  const winnerId = pending.tie ? picked : pending.inviterId;
  const winnerName =
    winnerId == null
      ? null
      : (standings.find((s) => s.inviterId === winnerId)?.name ??
        (winnerId === pending.inviterId ? pending.inviterName : null) ??
        'an outreacher no longer listed');
  const prize = hasCashPrize(c) ? `$${formatMoney(c.prizeAmount)}` : null;
  const nobodyScored = standings.length === 0 || (!pending.tie && pending.inviterId == null);
  const canApprove = !nobodyScored && winnerId != null;
  const busy = approveMut.isPending || rejectMut.isPending;
  const rankOf = (value: number): number => standings.findIndex((s) => s.value === value) + 1;

  function confirmApprove(): void {
    if (!canApprove) return;
    approveMut.mutate(
      { publicId: c.publicId, inviterId: pending.tie ? (picked ?? undefined) : undefined },
      {
        onSuccess: () => toast.success(prize ? `Approved: ${prize} to ${winnerName}` : `Approved ${winnerName} as the winner`),
        onError: (err: Error) => toast.error(`Couldn't approve the prize: ${err.message}`),
      },
    );
  }

  function confirmReject(): void {
    const why = reason.trim();
    if (!why) return;
    rejectMut.mutate(
      { publicId: c.publicId, reason: why },
      {
        onSuccess: () => toast.success('Prize rejected. Nothing was paid.'),
        onError: (err: Error) => toast.error(`Couldn't reject the prize: ${err.message}`),
      },
    );
  }

  return (
    <section
      aria-label={`Prize approval for ${c.name}`}
      className="mt-4 space-y-3 rounded-lg border border-warning/40 bg-warning/5 p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Trophy className="text-pending-strong h-4 w-4" aria-hidden /> Needs your approval
        </h3>
        <span className="text-xs text-muted-foreground">
          Final standings, frozen {formatDate(pending.at)} · {metricLabel(c.metric)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{windowCopy(c)}</p>

      {nobodyScored ? (
        <p className="text-sm text-muted-foreground">
          Nobody scored inside the challenge dates, so there is no winner to pay. Reject the prize to close it.
        </p>
      ) : (
        <ol className="space-y-1" aria-label="Final standings">
          {top.map((s) => {
            const lead = pending.tie ? tiedIds.has(s.inviterId) : s.inviterId === pending.inviterId;
            return (
              <li
                key={s.inviterId}
                className={cn(
                  'flex items-center gap-3 rounded px-2 py-1.5 text-sm',
                  lead ? 'bg-warning/10 font-medium' : 'odd:bg-muted/20',
                )}
              >
                <span className="w-6 text-center text-xs font-bold text-muted-foreground">{rankOf(s.value)}</span>
                <span className="flex-1 truncate">{s.name}</span>
                {lead && <Trophy className="text-pending-strong h-3.5 w-3.5" aria-label="First place" />}
                <span className="text-xs font-medium tabular-nums">{formatValue(c.metric, s.value)}</span>
              </li>
            );
          })}
          {standings.length > top.length && (
            <li className="px-2 text-xs text-muted-foreground">
              + {standings.length - top.length} more in the frozen standings
            </li>
          )}
        </ol>
      )}

      {!nobodyScored &&
        (pending.tie ? (
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">
              First place is tied. Pick the winner{prize ? ` of the ${prize} prize` : ''}:
            </legend>
            <div role="radiogroup" aria-label="Pick the winner" className="flex flex-wrap gap-2">
              {tied.map((s) => (
                <label
                  key={s.inviterId}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                    picked === s.inviterId ? 'border-brand bg-brand/10' : 'border-border hover:bg-accent/60',
                  )}
                >
                  <input
                    type="radio"
                    name={`winner-${c.publicId}`}
                    value={s.inviterId}
                    checked={picked === s.inviterId}
                    disabled={busy || step !== 'review'}
                    onChange={() => setPicked(s.inviterId)}
                    className="h-4 w-4 accent-[var(--brand)]"
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
          <p className="text-sm">
            Proposed winner: <strong>{winnerName}</strong>
            {prize ? (
              <>
                {' '}
                · <strong>{prize}</strong> prize
              </>
            ) : (
              ' · no cash prize'
            )}
          </p>
        ))}

      {step === 'review' && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!canApprove || busy} onClick={() => setStep('approve')}>
            <Trophy className="mr-1 h-3.5 w-3.5" aria-hidden /> Approve prize
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setStep('reject')}>
            <Ban className="mr-1 h-3.5 w-3.5" aria-hidden /> Reject
          </Button>
          {pending.tie && picked == null && !nobodyScored && (
            <span className="text-xs text-muted-foreground">Pick the winner to approve.</span>
          )}
        </div>
      )}

      {step === 'approve' && (
        <div role="group" aria-label="Confirm approval" className="space-y-2 rounded-md border border-border bg-card p-3">
          <p className="text-sm">
            {prize
              ? `Pay ${prize} to ${winnerName}? This creates the prize payout now and can't be undone here.`
              : `Approve ${winnerName} as the winner? There is no cash prize, so nothing is paid.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={confirmApprove}>
              {approveMut.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              {prize ? `Yes, pay ${prize}` : 'Yes, approve'}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStep('review')}>
              Back
            </Button>
          </div>
        </div>
      )}

      {step === 'reject' && (
        <div role="group" aria-label="Confirm rejection" className="space-y-2 rounded-md border border-border bg-card p-3">
          <Label htmlFor={`reject-${c.publicId}`}>Why reject this prize?</Label>
          <Input
            id={`reject-${c.publicId}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Standings include duplicate invites"
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">Nothing is paid and the challenge closes. The reason is kept.</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="destructive" disabled={busy || !reason.trim()} onClick={confirmReject}>
              {rejectMut.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Reject prize
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStep('review')}>
              Back
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function ChallengeFields({
  form,
  idPrefix,
  teamOptions,
  nicheOptions,
  onSubmit,
  onCancel,
  pending,
  submitLabel,
}: {
  form: UseFormReturn<ChallengeForm>;
  idPrefix: string;
  teamOptions: { value: string; label: string }[];
  nicheOptions: { value: string; label: string }[];
  onSubmit: (data: ChallengeForm) => void;
  onCancel: () => void;
  pending: boolean;
  submitLabel: string;
}): JSX.Element {
  const metric = form.watch('metric');
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input id={`${idPrefix}-name`} placeholder="Weekly posting race" {...form.register('name', { required: true })} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-desc`}>Description (optional)</Label>
        <Input
          id={`${idPrefix}-desc`}
          placeholder="Get the most creators posting this week"
          {...form.register('description')}
        />
      </div>

      <label className="grid gap-2">
        <span className="text-sm font-medium leading-none">Ranked by</span>
        <Controller
          control={form.control}
          name="metric"
          render={({ field }) => (
            <Combobox
              value={field.value}
              onChange={(v) => v && field.onChange(v as ChallengeMetric)}
              options={CHALLENGE_METRICS.map((m) => ({ value: m, label: METRIC_COPY[m].label }))}
              placeholder="Pick a metric"
            />
          )}
        />
        {metric && <span className="block text-xs font-normal text-muted-foreground">{METRIC_COPY[metric]?.hint}</span>}
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-2">
          <span className="text-sm font-medium leading-none">Team (optional)</span>
          <Controller
            control={form.control}
            name="targetTeam"
            render={({ field }) => (
              <Combobox
                value={field.value}
                onChange={field.onChange}
                options={teamOptions}
                allowAll
                allLabel="All teams"
                placeholder="All teams"
              />
            )}
          />
        </label>
        <label className="grid gap-2">
          <span className="text-sm font-medium leading-none">Niche (optional)</span>
          <Controller
            control={form.control}
            name="targetNiche"
            render={({ field }) => (
              <Combobox
                value={field.value}
                onChange={field.onChange}
                options={nicheOptions}
                allowAll
                allLabel="All niches"
                placeholder="All niches"
              />
            )}
          />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-start`}>Starts</Label>
          <Input id={`${idPrefix}-start`} type="datetime-local" {...form.register('startDate', { required: true })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-end`}>Ends</Label>
          <Input id={`${idPrefix}-end`} type="datetime-local" {...form.register('endDate', { required: true })} />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-prize-amount`}>Prize ($)</Label>
          <Input
            id={`${idPrefix}-prize-amount`}
            type="number"
            step="0.01"
            min="0"
            placeholder="500"
            {...form.register('prizeAmount')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-prize`}>Prize note (optional)</Label>
          <Input id={`${idPrefix}-prize`} placeholder="Bonus to the winner" {...form.register('prizeDescription')} />
        </div>
      </div>

      <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <AlertTriangle className="text-pending-strong mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <div className="space-y-1">
          <p>Standings count only activity between the start and end dates.</p>
          <p>
            Nothing is paid automatically. When the challenge ends its final standings are frozen and first place
            waits on this page for your approval. A paused challenge proposes no prize, and once standings are
            frozen the challenge can’t be edited.
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending}>
          {pending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}
